import React, { useState, useEffect, useRef, useContext, RefObject, MouseEvent } from "react";
import DrawerMenu from "./DrawerMenu";
import Link from "next/link";
import { useRouter } from "next/router";
import { theme, websiteColors } from "../../themes/Theme";
import { hostEventUrl } from "../../config/config";
import AuthContext from "../../contexts/AuthContext";
import dynamic from "next/dynamic";
import { handleOnLogin, successToaster } from "../../utils/utils";
import Cookies from "js-cookie";
import { Auth } from "aws-amplify";
import { removeCognitoFromLocalStorage } from "../../common/Function";
import { useMediaQuery } from "react-responsive";
import Loading from "../../components/Loading";
import AnimatedLogo from "../../common/AnimatedLogo";

const ProfileCard = dynamic(() => import("../../components/NavigationBar/ProfileCard"));

interface HomeNavProps {
	isBgNavyBlue?: boolean;
	isHomePage?: boolean;
	faqRef?: RefObject<HTMLElement>;
}

interface DropdownState {
	features: boolean;
	useCases: boolean;
	resources: boolean;
	pricing: boolean;
}

interface AuthContextType {
	signedIn: boolean | null;
	setAuth: (user: { attributes: any }, authenticated: boolean) => void;
}

const HomeNav: React.FC<HomeNavProps> = ({ isBgNavyBlue = false, isHomePage = false, faqRef }) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [hideProfile, setHideProfile] = useState<boolean>(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState<DropdownState>({
		features: false,
		useCases: false,
		resources: false,
		pricing: false
	});

	const buttonRefs: Record<keyof DropdownState, RefObject<HTMLDivElement>> = {
		features: useRef<HTMLDivElement>(null),
		useCases: useRef<HTMLDivElement>(null),
		resources: useRef<HTMLDivElement>(null),
		pricing: useRef<HTMLDivElement>(null)
	};

	const dropdownRefs: Record<keyof DropdownState, RefObject<HTMLDivElement>> = {
		features: useRef<HTMLDivElement>(null),
		useCases: useRef<HTMLDivElement>(null),
		resources: useRef<HTMLDivElement>(null),
		pricing: useRef<HTMLDivElement>(null)
	};

	const router = useRouter();
	const authContext = (useContext(AuthContext) as AuthContextType) || {
		signedIn: null,
		setAuth: () => { }
	};

	const handleLogout = async (): Promise<void> => {
		try {
			const message = "Logged Out Successfully";
			successToaster(message);
			authContext.setAuth({ attributes: {} }, false);

			const cookies = Cookies.get();
			Object.keys(cookies).forEach(key => {
				if (key.includes("CognitoIdentityServiceProvider")) {
					Cookies.remove(key);
				}
			});

			removeCognitoFromLocalStorage();
			Cookies.remove("authToken", { sameSite: "strict" });
			await Auth.signOut();
			// Optionally uncomment to redirect after logout:
			// router.replace("/", undefined, { shallow: false });
		} catch (err: any) {
			console.error(err.message);
		}
	};

	let smallerScreen: boolean = useMediaQuery({ maxWidth: 991 }, undefined, (matches: boolean) => {
		smallerScreen = matches;
	});

	const toggleDrawer = (): void => {
		setIsOpen(!isOpen);
	};

	const navStyle: React.CSSProperties = {
		borderBottom: "1px solid #d6d6d6",
		backgroundColor: isBgNavyBlue ? "#002E6E" : "#fff",
		color: "#fff !important"
	};

	const handleClickOutside: any = (event: MouseEvent<Document>): void => {
		let shouldClose = true;
		Object.keys(dropdownRefs).forEach(key => {
			const dropdown = dropdownRefs[key as keyof DropdownState].current;
			const button = buttonRefs[key as keyof DropdownState].current;
			const target = event.target as Node;
			if (dropdown && dropdown.contains(target)) shouldClose = false;
			if (button && button.contains(target)) shouldClose = false;
		});
		if (shouldClose) {
			setIsDropdownOpen({
				features: false,
				useCases: false,
				resources: false,
				pricing: false
			});
		}
	};

	useEffect(() => {
		const anyDropdownOpen: any = Object.values(isDropdownOpen).some(v => v);
		if (anyDropdownOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isDropdownOpen]);

	useEffect(() => {
		const handleRouteChange = (): void => {
			setIsDropdownOpen({
				features: false,
				useCases: false,
				resources: false,
				pricing: false
			});
		};
		router.events.on("routeChangeStart", handleRouteChange);
		return () => {
			router.events.off("routeChangeStart", handleRouteChange);
		};
	}, [router.events]);

	const scrollToFaq = (): void => {
		if (faqRef && faqRef.current) {
			faqRef.current.scrollIntoView({ behavior: "smooth" });
			setIsDropdownOpen({
				features: false,
				useCases: false,
				resources: false,
				pricing: false
			});
		}
	};

	return (
		<div>
			<header
				className="px-3 font-figtree px-sm-5 px-lg-5 py-2 py-sm-2 nav-container"
				style={navStyle}
			>
				<nav className="d-flex justify-content-between align-items-center text-pricing">
					<div className="left-navItem d-flex align-items-center gap-5">
						<div>
							<Link
								href="/"
								className="nav-link d-block"
								style={{ textDecoration: "none" }}
							>
								{isBgNavyBlue ? (
									<AnimatedLogo color="white" isFromNav />
								) : (
									<AnimatedLogo color="blue" isFromNav />
								)}
							</Link>
						</div>
						<ul className="d-none d-lg-flex gap-3 gap-xl-4 align-items-center list-unstyled flex-row my-0">
							<li
								style={{ position: "relative" }}
								className="d-flex text-nowrap font-sm figtree-family align-items-center fw-medium gap-1"
							>
								<Link
									href="https://konfhub.com/events/India?category=Marathon"
									className={`nav-link-item ${isBgNavyBlue ? "text-white" : "text-pricing"}`}
									target="_blank"
									rel="noreferrer"
								>
									Marathons
								</Link>
							</li>
							<li
								style={{ position: "relative" }}
								className="d-flex text-nowrap font-sm figtree-family align-items-center fw-medium gap-1"
							>
								<Link
									href="https://konfhub.com/pricing"
									className={`nav-link-item ${isBgNavyBlue ? "text-white" : "text-pricing"}`}
									target="_blank"
									rel="noreferrer"
								>
									Pricing
								</Link>
							</li>
						</ul>
					</div>
					<div className="right-navItem">
						<div className="d-none d-lg-flex align-items-center text-nowrap gap-4">
							{authContext.signedIn === null ? (
								<div className="d-flex justify-content-center align-items-center ms-3">
									<div className="me-5"></div>
									<Loading className={"m-auto"} color="#002e6e" />
								</div>
							) : authContext.signedIn === true ? (
								<div className="d-flex justify-content-center align-items-center align-items-lg-baseline ms-3">
									<ProfileCard
										onLogout={handleLogout}
										hideProfile={hideProfile}
									/>
								</div>
							) : (
								<div className="d-flex header-menu-item">
									<a
										onClick={(e: MouseEvent<HTMLAnchorElement>) =>
											handleOnLogin(e, authContext)
										}
										className={`login-btn text-decoration-none ${isBgNavyBlue ? "text-white" : "text-pricing"
											}`}
									>
										Log in
									</a>
								</div>
							)}
							<a
								target="_blank"
								rel="noopener noreferrer"
								href={`${hostEventUrl}/create/event`}
								className={`text-decoration-none ${isBgNavyBlue ? "host-btn-bt" : "host-btn-wt"
									}`}
							>
								Host a Marathon
							</a>
						</div>
						<div className="d-flex align-item-center gap-2">
							{!isBgNavyBlue ? (
								<img
									className="d-block d-lg-none"
									src="/img/menu.svg"
									alt="Menu"
									onClick={toggleDrawer}
								/>
							) : (
								<img
									className="d-block d-lg-none"
									src="https://d2nnrpx7gk2my2.cloudfront.net/files/670d4ef94208120008fdb367/_Navmenubutton.svg"
									alt="Menu"
									onClick={toggleDrawer}
								/>
							)}
						</div>
						{isOpen && (
							<DrawerMenu
								onClose={toggleDrawer}
								handleLogout={handleLogout}
								isHomePage={isHomePage}
							/>
						)}
					</div>
				</nav>
			</header>

			<style jsx>{`
				.nav-container {
					border-bottom: 1px solid #d6d6d6;
				}
				.dropdownicon {
					height: 20px;
					width: 20px;
					display: inline-block;
					cursor: pointer;
				}
				.icon-open-wt {
					background: url("/img/dropdown.svg") no-repeat;
					transform: rotate(-180deg);
				}
				.closed-icon-wt {
					background: url("/img/dropdown.svg") no-repeat;
				}
				.icon-open-bt {
					background: url("https://d2nnrpx7gk2my2.cloudfront.net/files/67041ca665e38f0008782282/chevron-down.svg")
						no-repeat;
					transform: rotate(-180deg);
				}
				.closed-icon-bt {
					background: url("https://d2nnrpx7gk2my2.cloudfront.net/files/67041ca665e38f0008782282/chevron-down.svg")
						no-repeat;
				}
				.dropdown-menusitem {
					position: absolute;
					top: 50px;
					left: ${isDropdownOpen.pricing ? "378px" : "206px"};
					background: white;
					border-radius: 2px;
					box-shadow: 0px 4px 10px 0px #002e6e1a;
					padding: 0px 20px 0px;
					z-index: 100;
					opacity: 1;
					transform: translateY(10px);
					transition:
						opacity 0.3s,
						transform 0.3s;
					display: block;
					max-width: ${isDropdownOpen.pricing ? "400px" : "950px"};
					width: 100%;
				}
				.dropdown-menus {
					position: absolute;
					top: 30px;
					left: -20px;
					background: white;
					border-radius: 8px;
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
					padding: 10px 10px;
					z-index: 100;
					width: 350px;
					opacity: 1;
					transform: translateY(10px);
					transition:
						opacity 0.3s,
						transform 0.3s;
					display: block;
				}
				.dropdown-item {
					display: flex;
					align-items: start;
					padding: 10px;
					margin-bottom: 5px;
				}
				.dropdown-item:hover {
					background: #c5e2ff3d;
					color: black;
					border-radius: 8px;
				}
				.dropdown-item img {
					width: 24px;
					height: 24px;
					margin-right: 12px;
				}
				.dropdown-item h6 {
					margin: 0;
					font-size: 16px;
				}
				.cursor-pointer {
					cursor: pointer;
				}
				.dropdown-item p {
					margin: 0;
					font-size: 12px;
					color: #666;
				}
				.host-btn-wt {
					padding: 8px 10px;
					border: 1px solid ${websiteColors[theme].textSecondary};
					border-radius: 4px;
					text-decoration: none;
					color: #0f0f0f;
					box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
					font-weight: 600;
					line-height: 28px;
				}
				.host-btn-bt {
					padding: 8px 10px;
					border: 1px solid #fff;
					border-radius: 4px;
					text-decoration: none;
					color: #fff;
					box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
					font-weight: 600;
					line-height: 28px;
				}
				@media screen and (max-width: 1045px) {
					.left-navItem {
						gap: 16px !important;
					}
				}
				.login-btn {
					cursor: pointer;
				}
				.nav-link-item {
					text-decoration: none !important;
					color: #424242 !important;
				}
			`}</style>
		</div>
	);
};

export default HomeNav;
